import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const QuarterForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [record, setRecord] = useState<Record | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const [reviewDate, setReviewDate] = useState(new Date());
  const [treatmentInit, setTreatmentInit] = useState(new Date());
  const [reviewAt, setReviewAt] = useState(new Date());
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);
        setRecord(recordResponse?.data);

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          if (formResponse?.data) {
            setReviewDate(
              formResponse?.data.answers.reviewDate
                ? moment(formResponse?.data.answers.reviewDate).toDate()
                : new Date(),
            );

            setTreatmentInit(
              formResponse?.data.answers.treatmentInit
                ? moment(formResponse?.data.answers.treatmentInit).toDate()
                : new Date(),
            );

            setReviewAt(
              formResponse?.data.answers.reviewAt
                ? moment(formResponse?.data.answers.reviewAt).toDate()
                : new Date(),
            );
          }
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });
        data.reviewDate = moment(reviewDate).format('YYYY-MM-DD');
        data.treatmentInit = moment(treatmentInit).format('YYYY-MM-DD');
        data.reviewAt = moment(reviewAt).format('YYYY-MM-DD');

        const formRequest = {
          type: 'quarterplan',
          title: 'Revisão Semestral Plano de Tratamento',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar revisão.',
          });
        }
      }
    },
    [
      api,
      record,
      history,
      formId,
      reviewAt,
      reviewDate,
      treatmentInit,
      id,
      signatures,
    ],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers || { record: record?.number }}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              REVISÃO SEMESTRAL DO PLANO DE TRATAMENTO
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Data da Revisão"
                    inputFormat="dd/MM/yyyy"
                    value={reviewDate}
                    onChange={(d) => setReviewDate(d as Date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Data do Início do Tratamento"
                    inputFormat="dd/MM/yyyy"
                    value={treatmentInit}
                    onChange={(d) => setTreatmentInit(d as Date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={4}>
                <Input id="record" label="Prontuário" name="record" />
              </Grid>

              <Grid item xs={2}>
                <Input id="age" label="Idade" name="age" />
              </Grid>

              <Grid item xs={5}>
                <Input
                  id="professional"
                  label="Terapeuta"
                  name="professional"
                />
              </Grid>

              <Grid item xs={5}>
                <Input
                  id="supervisor"
                  label="Supervisor ou Preceptor"
                  name="supervisor"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="current_diagnosis"
                  label="Impressão Diagnóstica Atual"
                  name="current_diagnosis"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="model"
                  label="Modalidade de Tratamento da criança"
                  name="model"
                />
              </Grid>

              <Grid item xs={12}>
                <Input id="frequency" label="Frequência" name="frequency" />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="parent_model"
                  label="Modalidade de Atendimento a pais "
                  name="parent_model"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="parent_frequency"
                  label="Frequência"
                  name="parent_frequency"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="status"
                  isMultiline
                  rows={5}
                  label="Status do Paciente (Progresso quanto aos objetivos do plano de tratamento, regressão, sintomas, mudanças situacionais, etc.)"
                  name="status"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="review"
                  isMultiline
                  rows={5}
                  label="Avaliação do Curso do Tratamento (Impedimentos, natureza da relação terapêutica, razões para melhora, etc.)"
                  name="review"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="changes"
                  isMultiline
                  rows={5}
                  label="Alterações do Plano de Tratamento e Razões Para as Mesmas (Novos objetivos, mudança na
                    modalidade de atendimento, freqüência, início do processo de alta, etc.)"
                  name="changes"
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={1} justify="center">
              <Grid item xs={10} />
              <Grid item xs={2} justify="flex-end">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Revisado em"
                    inputFormat="dd/MM/yyyy"
                    value={reviewAt}
                    onChange={(d) => setReviewAt(d as Date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(`/prontuarios/${id}`)}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default QuarterForm;
